import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { Facebook, Phone, Mail, Sparkles, Film, Youtube, Instagram, MoveRight, QrCode } from "lucide-react";
import { HOTLINE, EMAIL, FACEBOOK_URL, FACEBOOK_REEL, YOUTUBE_URL, INSTAGRAM_URL, WHATSAPP_CATALOG, WHATSAPP_BASE, OTA } from "@/lib/contact";
import Logo from "@/components/Logo";

export default function Footer() {
  const { t, lang } = useLang();
  const year = new Date().getFullYear();

  return (
    <footer className="bg-forest-900 text-cream-100/80" data-testid="site-footer">
      {/* "Find us on" — OTA strip */}
      <div className="border-b border-cream-100/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-7 flex flex-col md:flex-row items-start md:items-center gap-5 md:gap-10">
          <div className="text-[10px] uppercase tracking-widest2 text-gold-400 shrink-0">
            {lang === 'en' ? 'Find us on' : 'আমাদের পাবেন'}
          </div>
          <div className="flex flex-wrap items-center gap-3" data-testid="ota-strip">
            {OTA.map((o) => (
              <a key={o.name} href={o.url} target="_blank" rel="noreferrer"
                 data-testid={`ota-${o.name.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}
                 className="group inline-flex items-center gap-1.5 border border-cream-100/20 hover:border-gold-400/70 px-4 py-1.5 rounded-full text-[11px] tracking-wide text-cream-100/85 hover:text-cream-100 transition-colors">
                {o.name}
                <MoveRight className="w-3 h-3 opacity-60 group-hover:opacity-100 transition-opacity" />
              </a>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <Logo size={40} light linkTo={null} testid="footer-logo" />
            <p className="mt-6 text-sm leading-relaxed max-w-md">
              {t.footer.made}. Naljuri, Jaflong, Jaintapur, Sylhet — 3156, Bangladesh.
            </p>
            <a href={WHATSAPP_CATALOG} target="_blank" rel="noreferrer"
               data-testid="footer-catalog-link"
               className="mt-7 inline-flex items-center gap-2 bg-gold-600 text-cream-100 px-5 py-2.5 rounded-full text-xs hover:bg-gold-700 transition-colors border border-gold-500/40">
              <Sparkles className="w-3.5 h-3.5" /> {t.contact.catalog}
            </a>

            {/* Socials */}
            <div className="mt-7 flex flex-wrap items-center gap-3" data-testid="footer-socials">
              <SocialIcon icon={Facebook} href={FACEBOOK_URL} label="Facebook" testid="social-facebook" />
              <SocialIcon icon={Instagram} href={INSTAGRAM_URL} label="Instagram" testid="social-instagram" />
              <SocialIcon icon={Youtube} href={YOUTUBE_URL} label="YouTube" testid="social-youtube" />
              <SocialIcon icon={Film} href={FACEBOOK_REEL} label="Reel" testid="social-reel" />
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="text-[10px] uppercase tracking-widest2 text-gold-400 mb-4">Visit</div>
            <ul className="space-y-2.5 text-sm">
              <li><a href="#stay" className="hover:text-gold-400">Stay</a></li>
              <li><a href="#experiences" className="hover:text-gold-400">Experiences</a></li>
              <li><a href="#nearby" className="hover:text-gold-400">Nearby</a></li>
              <li><a href="#cricket" className="hover:text-gold-400">Cricket Field</a></li>
              <li><a href="#dining" className="hover:text-gold-400">Dining</a></li>
              <li><a href="#contact" className="hover:text-gold-400">Contact</a></li>
              <li><Link to="/admin/login" className="hover:text-gold-400" data-testid="footer-admin-link">Admin</Link></li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="text-[10px] uppercase tracking-widest2 text-gold-400 mb-4">Reach us</div>
            <ul className="space-y-3 text-sm">
              <li className="flex items-center gap-3"><Phone className="w-4 h-4 text-gold-400" /> <a href={`tel:${HOTLINE}`} className="hover:text-cream-100">{HOTLINE}</a></li>
              <li className="flex items-center gap-3"><Mail className="w-4 h-4 text-gold-400" /> <a href={`mailto:${EMAIL}`} className="hover:text-cream-100">{EMAIL}</a></li>
              <li className="flex items-center gap-3"><Youtube className="w-4 h-4 text-gold-400" /> <a href={YOUTUBE_URL} target="_blank" rel="noreferrer" className="hover:text-cream-100">@NaljuriinnOfficial</a></li>
              <li className="flex items-center gap-3"><Instagram className="w-4 h-4 text-gold-400" /> <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="hover:text-cream-100">@naljuri</a></li>
            </ul>

            {/* WhatsApp QR — scan to reserve */}
            <div className="mt-6 inline-flex items-center gap-4 p-3 rounded-2xl border border-sage-300/30 bg-forest-800/50 backdrop-blur-sm"
                 data-testid="footer-whatsapp-qr">
              <img src={`https://api.qrserver.com/v1/create-qr-code/?size=140x140&margin=2&bgcolor=1B3022&color=DDF6D2&data=${encodeURIComponent(WHATSAPP_BASE)}`}
                   alt="Scan to reserve your stay at Naljuri Inn via WhatsApp"
                   loading="lazy" width="88" height="88"
                   className="w-[88px] h-[88px] rounded-lg" />
              <div className="text-[11px] leading-snug">
                <div className="inline-flex items-center gap-1.5 text-sage-200 uppercase tracking-widest2 text-[9px]">
                  <QrCode className="w-3 h-3" /> {lang === 'en' ? 'Scan to reserve' : 'রিজার্ভ করতে স্ক্যান'}
                </div>
                <div className="mt-1 text-cream-100/80">
                  {lang === 'en' ? 'Opens WhatsApp with our concierge hotline.' : 'আমাদের কনসিয়ার্জের সঙ্গে WhatsApp খুলবে।'}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-cream-100/10 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-cream-100/50">
          <div>© {year} Naljuri Inn. {t.footer.rights}</div>
          <div className="font-mono tracking-widest2 uppercase text-gold-400/70">25.196°N · 92.018°E</div>
        </div>
      </div>
    </footer>
  );
}

const SocialIcon = ({ icon: Icon, href, label, testid }) => (
  <a href={href} target="_blank" rel="noreferrer" aria-label={label} data-testid={testid}
     className="w-10 h-10 rounded-full border border-cream-100/25 hover:border-gold-400 inline-flex items-center justify-center transition-colors hover:bg-cream-100/5">
    <Icon className="w-4 h-4 text-cream-100/85" />
  </a>
);
